import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  Paper,
  IconButton,
  Drawer,
  useMediaQuery,
  useTheme,
  AppBar,
  Toolbar,
} from "@mui/material";
import {
  Home as HomeIcon,
  Archive as ArchiveIcon,
  ViewQuilt as Square3Stack3DIcon,
  Photo as PhotoIcon,
  AttachMoney as BanknotesIcon,
  Storage as CircleStackIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
} from "@mui/icons-material";

const Leftmenu = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);

  // URL değiştiğinde mobil menüyü kapat
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const menuItems = [
    { text: "Ana Sayfa", icon: <HomeIcon />, path: "/" },
    { text: "Ürünler", icon: <ArchiveIcon />, path: "/products" },
    { text: "Kategoriler", icon: <Square3Stack3DIcon />, path: "/category" },
    { text: "Resimler", icon: <PhotoIcon />, path: "/mainpageupdates" },
    { text: "Fiyatlar", icon: <BanknotesIcon />, path: "/price" },
    { text: "Yedekleme", icon: <CircleStackIcon />, path: "/backup" },
  ];

  const MenuContent = () => (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Box
        sx={{
          height: isMobile ? 70 : 80,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 2,
          position: 'relative',
          flexShrink: 0,
        }}
      >
        <Box
          component="img"
          src="/LOGO.svg"
          alt="Logo"
          sx={{
            width: 'auto',
            maxWidth: '160px',
            height: 'auto',
          }}
        />
        {isMobile && (
          <IconButton
            onClick={handleDrawerToggle}
            sx={{
              position: 'absolute',
              right: 8,
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'primary.main',
            }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </Box>

      <List 
        sx={{ 
          width: '100%', 
          flex: 1, 
          overflowY: 'auto',
          overflowX: 'hidden',
          '&::-webkit-scrollbar': {
            width: '4px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: theme.palette.divider,
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: theme.palette.primary.light,
          },
        }}
      >
        {menuItems.map((item, index) => {
          const isSelected = location.pathname === item.path;
          
          return (
            <ListItem
              key={index}
              onClick={() => {
                navigate(item.path);
                if (isMobile) handleDrawerToggle();
              }}
              sx={{
                mb: 1,
                mx: 1,
                borderRadius: 2,
                cursor: 'pointer',
                bgcolor: isSelected ? 'primary.main' : 'transparent',
                color: isSelected ? 'white' : 'primary.main',
                transition: 'all 0.2s ease',
                '&:hover': {
                  bgcolor: isSelected ? 'primary.dark' : 'primary.lighter',
                  color: isSelected ? 'white' : 'primary.dark',
                  transform: 'translateX(8px)',
                  '& .MuiListItemIcon-root': {
                    color: 'inherit',
                  },
                },
              }}
            >
              <ListItemIcon 
                sx={{ 
                  minWidth: 40,
                  color: 'inherit',
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText 
                primary={item.text}
                sx={{
                  '& .MuiTypography-root': {
                    fontFamily: 'Inter',
                    fontWeight: 500,
                    fontSize: '0.95rem',
                  },
                }}
              />
            </ListItem>
          );
        })}
      </List>
    </Box>
  );

  return (
    <>
      {/* Mobil AppBar */}
      {isMobile && (
        <AppBar 
          position="fixed" 
          color="inherit" 
          elevation={1}
          sx={{
            width: '100%',
            height: 80,
          }}
        >
          <Toolbar sx={{ justifyContent: 'space-between', minHeight: 64 }}>
            <IconButton
              color="primary"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
            >
              <MenuIcon />
            </IconButton>
            <Box
              component="img"
              src="/LOGO.svg"
              alt="Logo"
              sx={{
                height: 80,
                width: 'auto',
              }}
            />
            <Box width={40} /> {/* Sağ tarafta boşluk için */}
          </Toolbar>
        </AppBar>
      )}

      {/* Mobil Drawer */}
      {isMobile ? (
        <Drawer
          variant="temporary"
          anchor="left"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            '& .MuiDrawer-paper': {
              width: 280,
              boxSizing: 'border-box',
              bgcolor: 'background.paper',
              borderRight: '1px solid',
              borderColor: 'divider',
              boxShadow: 3,
              overflow: 'hidden',
            },
          }}
        >
          <MenuContent />
        </Drawer>
      ) : (
        // Desktop Sidebar
        <Paper
          elevation={0}
          sx={{
            width: 200,
            height: '100vh',
            position: 'fixed',
            left: 0,
            top: 0,
            bgcolor: 'background.paper',
            borderRight: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <MenuContent />
        </Paper>
      )}
    </>
  );
};

export default Leftmenu;
